import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import PageviewTracker from "./components/PageviewTracker";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import CodeInjection from "./components/CodeInjection";
import SEOManager from "./components/SEOManager";
import FFLoader from "./components/FFLoader";

// ─── Code splitting ────────────────────────────────────────────────
// Landing is lazy-split so it doesn't drag its ~200 KiB of marketing
// UI into the main bundle for every route on the site. The Suspense
// fallback below (LandingHeroShell) re-renders the same hero markup
// using the .ff-preboot-* classes already inlined in index.html, so
// there's no visible flash between the raw HTML shell and the React
// tree — the fallback swaps the pre-boot shell 1:1, then the real
// Landing chunk swaps IN PLACE over the same layout.
// LandingHeroShell must stay eager (tiny — one JSX file, no icon or
// UI-lib imports) since it's the Suspense fallback.
import LandingHeroShell from "./pages/LandingHeroShell";

const lazyPage = (importer) => React.lazy(importer);

const Landing        = lazyPage(() => import("./pages/Landing"));

// Auth flows
const Login          = lazyPage(() => import("./pages/Login"));
const Signup         = lazyPage(() => import("./pages/Signup"));
const SsoSuccess     = lazyPage(() => import("./pages/SsoSuccess"));
const VerifyEmail    = lazyPage(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazyPage(() => import("./pages/ForgotPassword"));
const ResetPassword  = lazyPage(() => import("./pages/ResetPassword"));

// App surfaces
const Dashboard      = lazyPage(() => import("./pages/Dashboard"));
const FormBuilder    = lazyPage(() => import("./pages/FormBuilder"));
const FormView       = lazyPage(() => import("./pages/FormView"));
const PaymentReturn  = lazyPage(() => import("./pages/PaymentReturn"));
const Submissions    = lazyPage(() => import("./pages/Submissions"));
const Settings       = lazyPage(() => import("./pages/Settings"));
const Admin          = lazyPage(() => import("./pages/Admin"));
const Help           = lazyPage(() => import("./pages/Help"));
const Upgrade        = lazyPage(() => import("./pages/Upgrade"));
const Analytics      = lazyPage(() => import("./pages/Analytics"));
const Templates      = lazyPage(() => import("./pages/Templates"));
const TemplatePage   = lazyPage(() => import("./pages/TemplatePage"));
const Teams          = lazyPage(() => import("./pages/Teams"));
const Security       = lazyPage(() => import("./pages/Security"));
const Trust          = lazyPage(() => import("./pages/Trust"));
const PressKit       = lazyPage(() => import("./pages/PressKit"));
const Skills         = lazyPage(() => import("./pages/Skills"));
const About          = lazyPage(() => import("./pages/About"));
const InvitationDecline = lazyPage(() => import("./pages/InvitationDecline"));
const CliLanding     = lazyPage(() => import("./pages/CliLanding"));
const AdminAuditLog  = lazyPage(() => import("./pages/AdminAuditLog"));

// Integrations (one chunk each — most visitors hit at most one)
const Integrations              = lazyPage(() => import("./pages/Integrations"));
const SimpleIntegration         = lazyPage(() => import("./pages/SimpleIntegration"));
const GoogleSheetsPage          = lazyPage(() => import("./pages/GoogleSheetsPage"));
const SlackIntegrationPage      = lazyPage(() => import("./pages/SlackIntegrationPage"));
const DiscordIntegrationPage    = lazyPage(() => import("./pages/DiscordIntegrationPage"));
const NotionIntegrationPage     = lazyPage(() => import("./pages/NotionIntegrationPage"));
const AirtableIntegrationPage   = lazyPage(() => import("./pages/AirtableIntegrationPage"));
const HubSpotIntegrationPage    = lazyPage(() => import("./pages/HubSpotIntegrationPage"));
const TelegramIntegrationPage   = lazyPage(() => import("./pages/TelegramIntegrationPage"));
const MSTeamsIntegrationPage    = lazyPage(() => import("./pages/MSTeamsIntegrationPage"));
const MailchimpIntegrationPage  = lazyPage(() => import("./pages/MailchimpIntegrationPage"));
const KitIntegrationPage        = lazyPage(() => import("./pages/KitIntegrationPage"));
const BrevoIntegrationPage      = lazyPage(() => import("./pages/BrevoIntegrationPage"));
const KlaviyoIntegrationPage    = lazyPage(() => import("./pages/KlaviyoIntegrationPage"));
const CallRailIntegrationPage   = lazyPage(() => import("./pages/CallRailIntegrationPage"));
const CTMIntegrationPage        = lazyPage(() => import("./pages/CTMIntegrationPage"));
const WhatConvertsIntegrationPage = lazyPage(() => import("./pages/WhatConvertsIntegrationPage"));
const ActiveCampaignIntegrationPage = lazyPage(() => import("./pages/ActiveCampaignIntegrationPage"));
const PipedriveIntegrationPage  = lazyPage(() => import("./pages/PipedriveIntegrationPage"));
const ZohoCRMIntegrationPage    = lazyPage(() => import("./pages/ZohoCRMIntegrationPage"));
const IntercomIntegrationPage   = lazyPage(() => import("./pages/IntercomIntegrationPage"));
const N8nIntegrationPage        = lazyPage(() => import("./pages/N8nIntegrationPage"));
const MakeIntegrationPage       = lazyPage(() => import("./pages/MakeIntegrationPage"));
const ZapierIntegrationPage     = lazyPage(() => import("./pages/ZapierIntegrationPage"));

// Marketing & content
const ApiPage          = lazyPage(() => import("./pages/ApiPage"));
const WordPressLanding = lazyPage(() => import("./pages/WordPressLanding"));
const KnowledgeBase    = lazyPage(() => import("./pages/KnowledgeBase"));
const Blog             = lazyPage(() => import("./pages/Blog"));
const BlogPost         = lazyPage(() => import("./pages/BlogPost"));
const NotFound         = lazyPage(() => import("./pages/NotFound"));

// Legal: shared chunk, one module exports all six routes
// Legal pages are tiny — re-import the named exports lazily but as
// a single shared chunk so we don't fragment.
const PrivacyPolicy          = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService         = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.TermsOfService })));
const CookiePolicy           = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.CookiePolicy })));
const DataProcessingAgreement = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.DataProcessingAgreement })));
const GDPRCompliance         = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.GDPRCompliance })));
const AccessibilityStatement = lazyPage(() => import("./pages/Legal").then(m => ({ default: m.AccessibilityStatement })));

// Tools (each is its own chunk — visitors hit at most one)
const Tools                    = lazyPage(() => import("./pages/Tools"));
const UtmBuilder               = lazyPage(() => import("./pages/tools/UtmBuilder"));
const LlmsTxtGenerator         = lazyPage(() => import("./pages/tools/LlmsTxtGenerator"));
const BulkRootDomainExtractor  = lazyPage(() => import("./pages/tools/BulkRootDomainExtractor"));
const BulkDomainAvailability   = lazyPage(() => import("./pages/tools/BulkDomainAvailability"));
const BulkEmailFinder          = lazyPage(() => import("./pages/tools/BulkEmailFinder"));
const BulkUrlOpener            = lazyPage(() => import("./pages/tools/BulkUrlOpener"));
const RedirectMapper           = lazyPage(() => import("./pages/tools/RedirectMapper"));
const BrokenLinkScanner        = lazyPage(() => import("./pages/tools/BrokenLinkScanner"));
const BulkHttpHeaderCheck      = lazyPage(() => import("./pages/tools/BulkHttpHeaderCheck"));
const WebhookPayloadTester     = lazyPage(() => import("./pages/tools/WebhookPayloadTester"));
const ImportGoogleForms        = lazyPage(() => import("./pages/tools/ImportGoogleForms"));
const ImportTally              = lazyPage(() => import("./pages/tools/ImportTally"));
const ImportTypeform           = lazyPage(() => import("./pages/tools/ImportTypeform"));
const ImportJotForm            = lazyPage(() => import("./pages/tools/ImportJotForm"));
const ImportWufoo              = lazyPage(() => import("./pages/tools/ImportWufoo"));
const ImportFormsite           = lazyPage(() => import("./pages/tools/ImportFormsite"));
const ImportFormspree          = lazyPage(() => import("./pages/tools/ImportFormspree"));
const ImportPaperform          = lazyPage(() => import("./pages/tools/ImportPaperform"));
const Import123FormBuilder     = lazyPage(() => import("./pages/tools/Import123FormBuilder"));
const ImportCognitoForms       = lazyPage(() => import("./pages/tools/ImportCognitoForms"));
const ImportFormstack          = lazyPage(() => import("./pages/tools/ImportFormstack"));
const ImportFillout            = lazyPage(() => import("./pages/tools/ImportFillout"));
const TextEditor               = lazyPage(() => import("./pages/tools/TextEditor"));
const KeywordConcatenation     = lazyPage(() => import("./pages/tools/KeywordConcatenation"));
const LogoFinder               = lazyPage(() => import("./pages/tools/LogoFinder"));
const BulkLlmPromptQuery       = lazyPage(() => import("./pages/tools/BulkLlmPromptQuery"));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FFLoader size={48} />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // 2FA enforcement gate — set by the workspace owner on the Pro plan.
  // We redirect every protected surface to /security UNTIL the user
  // enables TOTP 2FA on their account. /security is exempted from the
  // gate so the user can actually complete the setup. We also allow
  // /logout in case they want to bail.
  if (user?.requires_2fa_setup && location.pathname !== '/security') {
    return <Navigate to="/security?required=1" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FFLoader size={48} />
      </div>
    );
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FFLoader size={48} />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <PageviewTracker />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <FFLoader size={48} />
        </div>
      }>
      <Routes>
      {/* Public Routes */}
      {/* Landing has its own inner Suspense boundary so its fallback
          is the identical hero markup (LandingHeroShell) instead of
          the generic FFLoader spinner. This preserves the pre-boot
          shell paint across the React handoff — no visible flash. */}
      <Route path="/" element={
        <Suspense fallback={<LandingHeroShell />}>
          <Landing />
        </Suspense>
      } />
      <Route path="/wordpress" element={<WordPressLanding />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <Signup />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* SSO callback landing — reads token from URL fragment and
          persists it before redirecting to /dashboard. Not gated by
          PublicRoute because the whole point is to *become* logged in. */}
      <Route path="/sso/success" element={<SsoSuccess />} />
      
      {/* Public Form View */}
      <Route path="/f/:formId" element={<FormView />} />
      <Route path="/f/:formId/preview" element={<FormView />} />
      <Route path="/payment/return" element={<PaymentReturn />} />

      {/* Free tool pages — public, indexable, no auth */}
      <Route path="/tools" element={<Tools />} />
      <Route path="/tools/utm-builder" element={<UtmBuilder />} />
      <Route path="/tools/llms-txt-generator" element={<LlmsTxtGenerator />} />
      <Route path="/tools/bulk-root-domain-extractor" element={<BulkRootDomainExtractor />} />
      <Route path="/tools/bulk-domain-availability" element={<BulkDomainAvailability />} />
      <Route path="/tools/bulk-email-finder" element={<BulkEmailFinder />} />
      <Route path="/tools/bulk-url-opener" element={<BulkUrlOpener />} />
      <Route path="/tools/redirect-mapper" element={<RedirectMapper />} />
      <Route path="/tools/broken-link-scanner" element={<BrokenLinkScanner />} />
      <Route path="/tools/bulk-http-header-check" element={<BulkHttpHeaderCheck />} />
      <Route path="/tools/webhook-payload-tester" element={<WebhookPayloadTester />} />
      <Route path="/tools/import-from-google-forms" element={<ImportGoogleForms />} />
      <Route path="/tools/import-from-tally" element={<ImportTally />} />
      <Route path="/tools/import-from-typeform" element={<ImportTypeform />} />
      <Route path="/tools/import-from-jotform" element={<ImportJotForm />} />
      <Route path="/tools/import-from-wufoo" element={<ImportWufoo />} />
      <Route path="/tools/import-from-formsite" element={<ImportFormsite />} />
      <Route path="/tools/import-from-formspree" element={<ImportFormspree />} />
      <Route path="/tools/import-from-paperform" element={<ImportPaperform />} />
      <Route path="/tools/import-from-123formbuilder" element={<Import123FormBuilder />} />
      <Route path="/tools/import-from-cognito-forms" element={<ImportCognitoForms />} />
      <Route path="/tools/import-from-formstack" element={<ImportFormstack />} />
      <Route path="/tools/import-from-fillout" element={<ImportFillout />} />
      <Route path="/tools/text-editor" element={<TextEditor />} />
      <Route path="/tools/keyword-concatenation" element={<KeywordConcatenation />} />
      <Route path="/tools/logo-finder" element={<LogoFinder />} />
      <Route path="/tools/bulk-llm-prompt-query" element={<BulkLlmPromptQuery />} />
      
      {/* Help Pages - accessible to all */}
      <Route path="/help" element={<Help />} />
      <Route path="/knowledge-base" element={<KnowledgeBase />} />
      <Route path="/cli" element={<CliLanding />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      
      {/* Legal Pages - accessible to all */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/accessibility" element={<AccessibilityStatement />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/dpa" element={<DataProcessingAgreement />} />
      <Route path="/gdpr" element={<GDPRCompliance />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/builder/:formId" element={
        <ProtectedRoute>
          <FormBuilder />
        </ProtectedRoute>
      } />
      <Route path="/submissions" element={
        <ProtectedRoute>
          <Submissions />
        </ProtectedRoute>
      } />
      <Route path="/submissions/:formId" element={
        <ProtectedRoute>
          <Submissions />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/feature-request" element={
        <ProtectedRoute>
          <Help />
        </ProtectedRoute>
      } />
      <Route path="/bug-report" element={
        <ProtectedRoute>
          <Help />
        </ProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <ProtectedRoute>
          <Upgrade />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/templates" element={<Templates />} />
      <Route path="/templates/:slug" element={<TemplatePage />} />
      <Route path="/team" element={
        <ProtectedRoute>
          <Teams />
        </ProtectedRoute>
      } />
      <Route path="/security" element={
        <ProtectedRoute>
          <Security />
        </ProtectedRoute>
      } />
      <Route path="/trust" element={<Trust />} />
      <Route path="/press" element={<PressKit />} />
      <Route path="/press-kit" element={<PressKit />} />
      <Route path="/skills" element={<Skills />} />
      <Route path="/about" element={<About />} />
      <Route path="/invitations/:id/decline" element={<InvitationDecline />} />
      <Route path="/integrations" element={
        <ProtectedRoute>
          <Integrations />
        </ProtectedRoute>
      } />
      {/* Unified configuration page for all "simple" (API-key style) integrations.
          One slug → one page, schema driven from the backend registry.
          Mounted at /integrations/:provider so simple integrations share the
          same flat URL shape as hero integrations. React Router v6 ranks
          static segments above dynamic ones, so /integrations/slack etc.
          still hit their dedicated hero pages below. */}
      <Route path="/integrations/:provider" element={
        <ProtectedRoute>
          <SimpleIntegration />
        </ProtectedRoute>
      } />
      <Route path="/integrations/google-sheets" element={
        <ProtectedRoute>
          <GoogleSheetsPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/slack" element={
        <ProtectedRoute>
          <SlackIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/discord" element={
        <ProtectedRoute>
          <DiscordIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/notion" element={
        <ProtectedRoute>
          <NotionIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/airtable" element={
        <ProtectedRoute>
          <AirtableIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/hubspot" element={
        <ProtectedRoute>
          <HubSpotIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/telegram" element={
        <ProtectedRoute>
          <TelegramIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/microsoft-teams" element={
        <ProtectedRoute>
          <MSTeamsIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/mailchimp" element={
        <ProtectedRoute>
          <MailchimpIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/kit" element={
        <ProtectedRoute>
          <KitIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/brevo" element={
        <ProtectedRoute>
          <BrevoIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/klaviyo" element={
        <ProtectedRoute>
          <KlaviyoIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/callrail" element={
        <ProtectedRoute>
          <CallRailIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/calltrackingmetrics" element={
        <ProtectedRoute>
          <CTMIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/whatconverts" element={
        <ProtectedRoute>
          <WhatConvertsIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/activecampaign" element={
        <ProtectedRoute>
          <ActiveCampaignIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/pipedrive" element={
        <ProtectedRoute>
          <PipedriveIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/zoho-crm" element={
        <ProtectedRoute>
          <ZohoCRMIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/intercom" element={
        <ProtectedRoute>
          <IntercomIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/n8n" element={
        <ProtectedRoute>
          <N8nIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/make" element={
        <ProtectedRoute>
          <MakeIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/zapier" element={
        <ProtectedRoute>
          <ZapierIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/developer" element={
        <ProtectedRoute>
          <ApiPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <Admin />
        </AdminRoute>
      } />
      <Route path="/admin/audit-log" element={
        <AdminRoute>
          <AdminAuditLog />
        </AdminRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="App">
            <SEOManager />
            <CodeInjection />
            <AppRoutes />
            <Toaster position="top-right" richColors toastOptions={{ classNames: { success: '!text-white' } }} />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
